import React, { useState, useCallback } from 'react';
import { apiFetch, getApiHost } from '../utils/api';
import { trackEvent, trackError } from '../utils/analytics';
import { embeddedCheckoutEnabled } from '../utils/stripe';
import EmbeddedCheckoutModal from './EmbeddedCheckoutModal';

interface Props {
  onPurchaseStarted?: () => void;
}

const BUNDLES = [
  { credits: 1,   discount: 0,    label: '1 Credit' },
  { credits: 10,  discount: 0.05, label: '10 Credits (5% off)' },
  { credits: 50,  discount: 0.10, label: '50 Credits (10% off)' },
  { credits: 250, discount: 0.15, label: '250 Credits (15% off)' },
] as const;
const USD_PER_CREDIT = 0.15;

const FiatBuyCredits: React.FC<Props> = ({ onPurchaseStarted }) => {
  const [selected, setSelected] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);

  const bundle = BUNDLES[selected];
  const totalUsd = +(USD_PER_CREDIT * bundle.credits * (1 - bundle.discount)).toFixed(2);

  // In-site checkout: fetch the embedded Checkout Session client_secret.
  const fetchClientSecret = useCallback(async (): Promise<string> => {
    const res = await apiFetch(`${getApiHost()}/api/stripe/create-credit-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credits: bundle.credits, embedded: true }),
    });
    const data = await res.json().catch(() => ({} as any));
    if (!res.ok || !data.clientSecret) {
      const e = new Error(data.error || `Checkout failed (HTTP ${res.status})`);
      trackError(e, { flow: 'fiat_credit_checkout_embedded', bundle: bundle.credits, status: res.status });
      throw e;
    }
    onPurchaseStarted?.();
    return data.clientSecret;
  }, [bundle.credits, onPurchaseStarted]);

  const handleCheckout = async () => {
    trackEvent('buy_credits_clicked', { method: 'fiat', bundle: bundle.credits, amountUsd: totalUsd });
    // Preferred: in-site embedded checkout (no redirect). Falls back to the
    // hosted Stripe page when the publishable key isn't configured.
    if (embeddedCheckoutEnabled) { setShowCheckout(true); return; }
    setLoading(true);
    setError(null);
    let status = 0;
    try {
      const res = await apiFetch(`${getApiHost()}/api/stripe/create-credit-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credits: bundle.credits }),
      });
      status = res.status;
      const data = await res.json().catch(() => ({} as any));
      if (!res.ok || !data.url) throw new Error(data.error || `Checkout failed (HTTP ${res.status})`);
      onPurchaseStarted?.();
      window.location.href = data.url;
    } catch (err) {
      // Report so the broken-funnel (182 clicks / ~0 completions) is diagnosable:
      // is it throwing, and with what status?
      trackError(err, { flow: 'fiat_credit_checkout', bundle: bundle.credits, amountUsd: totalUsd, status });
      setError(err instanceof Error ? err.message : 'Checkout failed');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {BUNDLES.map((b, i) => {
          const usd = +(USD_PER_CREDIT * b.credits * (1 - b.discount)).toFixed(2);
          return (
            <button
              key={b.credits}
              onClick={() => setSelected(i)}
              className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left ${
                selected === i
                  ? 'border-crypto-accent bg-crypto-accent/10'
                  : 'border-gray-700 bg-crypto-dark hover:border-gray-600'
              }`}
            >
              <div>
                <span className="text-white font-medium">{b.label}</span>
              </div>
              <div className="text-right">
                <div className="text-white font-mono text-sm">${usd.toFixed(2)}</div>
                {b.discount > 0 && (
                  <div className="text-crypto-accent text-xs">Save {Math.round(b.discount * 100)}%</div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={handleCheckout}
        disabled={loading}
        className="w-full bg-crypto-accent hover:bg-crypto-accent/80 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-crypto-dark font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="animate-spin w-4 h-4 border-2 border-crypto-dark border-t-transparent rounded-full" />
            <span>Redirecting…</span>
          </>
        ) : (
          <>
            <span>💳</span>
            <span>Buy {bundle.credits} Credit{bundle.credits === 1 ? '' : 's'} for ${totalUsd.toFixed(2)}</span>
          </>
        )}
      </button>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <p className="text-gray-500 text-xs text-center">
        Secure checkout via Stripe. Credits are granted automatically after payment.
      </p>

      {showCheckout && (
        <EmbeddedCheckoutModal
          title={`Buy ${bundle.credits} Credit${bundle.credits === 1 ? '' : 's'} · $${totalUsd.toFixed(2)}`}
          fetchClientSecret={fetchClientSecret}
          onClose={() => setShowCheckout(false)}
        />
      )}
    </div>
  );
};

export default FiatBuyCredits;
